import type {ShiftEntity, TransportAssocEntity, TransportCategory} from "@ots-frontend/ots-data";
import {GridCellKind, type DataEditorProps} from "@glideapps/glide-data-grid";
// eslint-disable-next-line @nx/enforce-module-boundaries -- Shared theme-aware header sprites.
import {getDataGridSortIcons} from "@ots-frontend/ots-ui-tw";
import {getDriverShiftContext} from "../DispatchDataGrid/shift-context";
import {getDataGridSeparatorTheme} from "../DataGrid/dataGridTheme";
import {UseEntityDataGridProps} from "../EntityDataGrid/useEntityDataGrid";
import {useDataGrid} from "../DataGrid/useDataGrid";
import {useDataGridColumnLayout} from "../DataGrid/useDataGridColumnLayout";
import {useTicketDataGridCols} from "./useTicketDataGridCols";
import {useCallback, useMemo, useRef} from "react";
import type {ColumnSort} from "@ots-frontend/ots-ui-tw";
import {getDataGridDisplayRows, getDataGridSeparatorColumns} from "../DataGrid/dataGridSeparators";
import {getTicketSeparator, TicketSeparatorMode} from "./ticketDataGridSeparators";
import {useTicketSeparatorMode} from "./useTicketSeparatorMode";

export const EMPTY_ARRAY: TransportAssocEntity[] = [];
const EMPTY_SHIFTS: ShiftEntity[] = [];

export type UseTicketDataGridProps = Omit<UseEntityDataGridProps<TransportAssocEntity>, "colsMap"> & {
  category?: TransportCategory;
  /** Override for a ticket grid that should have its own saved layout. Category is appended. */
  columnStorageKey?: string;
  defaultSeparatorMode?: TicketSeparatorMode;
  shifts?: ShiftEntity[];
  appDttm?: number;
  colorScheme?: "light" | "dark";
};

export function useTicketDataGrid(props: UseTicketDataGridProps) {
  const {
    data = EMPTY_ARRAY,
    colWidths,
    category,
    sort,
    columnStorageKey = "ots.ticket-grid.columns",
    defaultSeparatorMode,
    shifts = EMPTY_SHIFTS,
    appDttm,
    colorScheme = "light",
  } = props;
  const headerIcons = useMemo(() => getDataGridSortIcons(colorScheme), [colorScheme]);
  const separatorTheme = useMemo(() => getDataGridSeparatorTheme(colorScheme), [colorScheme]);
  const {colsMap} = useTicketDataGridCols({colWidths, category});
  const storageKey = `${columnStorageKey}.${category?.code === "OIL" ? "oil" : "standard"}.v1`;
  // Legacy onSort feedback must not redefine the default each time a header is clicked.
  const initialSorts = useRef(new Map<string, ColumnSort | undefined>());
  if (!initialSorts.current.has(storageKey)) {
    const column = sort && colsMap.filter((column) => column.defaultVisible !== false)[sort.col];
    initialSorts.current.set(storageKey, column && sort ? {id: column.id, direction: sort.direction} : undefined);
  }
  const columnLayout = useDataGridColumnLayout(colsMap, storageKey, {
    defaultSort: initialSorts.current.get(storageKey),
  });
  const separatorControls = useTicketSeparatorMode(`${storageKey}.separator`, defaultSeparatorMode);
  const {mode: separatorMode} = separatorControls;
  const displayRows = useMemo(
    () =>
      getDataGridDisplayRows(
        data,
        separatorMode === "NONE"
          ? undefined
          : (ticket) => getTicketSeparator(ticket, separatorMode) ?? {value: "UNKNOWN", displayValue: "Unknown"}
      ),
    [data, separatorMode]
  );
  const shiftContexts = useMemo(
    () =>
      new Map(
        displayRows.flatMap((row) =>
          row.type === "SEPARATOR" && separatorMode === "DRIVER" && row.separator.value.startsWith("id:")
            ? [[row.separator.value, getDriverShiftContext(row.separator.value.slice(3), shifts, appDttm)] as const]
            : []
        )
      ),
    [displayRows, separatorMode, shifts, appDttm]
  );
  const columns = useMemo(
    () =>
      getDataGridSeparatorColumns(
        columnLayout.visibleColumns,
        separatorMode !== "NONE",
        separatorMode === "DRIVER"
          ? (separator, index, columnCount) => {
              const context = shiftContexts.get(separator.value);
              const copyData = [separator.displayValue, context?.status, context?.equipment, context?.shiftWindow]
                .filter(Boolean)
                .join(" | ");
              return index === 0
                ? {
                    kind: GridCellKind.Custom,
                    data: {
                      kind: "dispatch-separator-cell",
                      title: separator.displayValue,
                      shiftStatus: context?.status,
                      equipment: context?.equipment,
                      shiftWindow: context?.shiftWindow,
                      colorScheme,
                    },
                    copyData,
                    readonly: true,
                    allowOverlay: false,
                    span: [0, columnCount - 1],
                    themeOverride: separatorTheme,
                  }
                : {
                    kind: GridCellKind.Text,
                    data: "",
                    displayData: "",
                    readonly: true,
                    allowOverlay: false,
                    span: [0, columnCount - 1],
                    themeOverride: separatorTheme,
                  };
            }
          : undefined,
        separatorTheme
      ),
    [columnLayout.visibleColumns, separatorMode, shiftContexts, colorScheme, separatorTheme]
  );
  const dataGrid = useDataGrid({
    colsMap: columns,
    data: displayRows,
    columnSort: columnLayout.columnSort,
    onColumnResize: columnLayout.onColumnResize,
    disableSorting: separatorMode !== "NONE",
  });
  const {getOriginalIndex: getDisplayIndex, onHeaderMenuClick} = dataGrid;
  const onHeaderClicked = useCallback<NonNullable<DataEditorProps["onHeaderClicked"]>>(
    (index, event) => {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.isEdge) return;
      onHeaderMenuClick?.(index, event.bounds);
    },
    [onHeaderMenuClick]
  );
  const displayedColumns = useMemo(
    () =>
      dataGrid.columns.map((column, index) => ({
        ...column,
        icon: dataGrid.sort?.col === index ? `ots-sort-${dataGrid.sort.direction}` : column.icon,
      })),
    [dataGrid.columns, dataGrid.sort]
  );
  const getOriginalIndex = useCallback(
    (row: number) => {
      const value = displayRows[getDisplayIndex(row)];
      return value?.type === "DATA" ? value.sourceIndex : -1;
    },
    [displayRows, getDisplayIndex]
  );
  const getSeparator = useCallback(
    (row: number) => {
      const value = displayRows[getDisplayIndex(row)];
      return value?.type === "SEPARATOR" ? value.separator : undefined;
    },
    [displayRows, getDisplayIndex]
  );
  const rowOrder = JSON.stringify([
    separatorMode,
    dataGrid.sort?.direction,
    dataGrid.sort ? columns[dataGrid.sort.col]?.id : undefined,
    displayRows.map((_, index) => {
      const row = displayRows[getDisplayIndex(index)];
      return row?.type === "DATA" ? [row.sourceIndex, row.value.id] : row?.separator.value;
    }),
  ]);
  return {
    ...dataGrid,
    columns: displayedColumns,
    headerIcons,
    onHeaderClicked: separatorMode === "NONE" ? onHeaderClicked : undefined,
    getOriginalIndex,
    getSeparator,
    rowOrder,
    separatorControls,
    onColumnMoved: columnLayout.onColumnMoved,
    columnLayout,
  };
}

export type UseTicketDataGridResult = ReturnType<typeof useTicketDataGrid>;
